import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Switch from "@khanacademy/wonder-blocks-switch";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {
    getDefaultPersonaForProfile,
    useChangeStudentEmulationForUser,
} from "@khan/guide-core";
import {useGuideState} from "@khan/guide-state";
import {UserPersona} from "@khan/guide-types";
import {useUserAttributes} from "@khan/shared-core";

import graduationCapIcon from "@phosphor-icons/core/regular/graduation-cap.svg";

interface Props {
    threadId?: string | null;
    light?: boolean;
}

/**
 * Allow a user to switch between their default mode and student mode.
 */
export const AsStudentToggle = ({
    threadId,
    light = true,
}: Props): React.ReactElement => {
    const userAsStudent = useGuideState("userAsStudent");
    const changeStudentEmulationForTeacher = useChangeStudentEmulationForUser();
    const userAttributes = useUserAttributes();

    const tooltipContentString = userAsStudent
        ? t`Student mode ON`
        : t`Student mode OFF`;

    const handleToggle = () => {
        changeStudentEmulationForTeacher(!userAsStudent);
        const defaultPersona =
            userAttributes && getDefaultPersonaForProfile(userAttributes);
        const targetMode = defaultPersona
            ? userAsStudent
                ? defaultPersona === UserPersona.Teacher
                    ? "TEACHER"
                    : defaultPersona === UserPersona.Parent
                      ? "PARENT"
                      : null
                : "STUDENT"
            : null;
        if (targetMode) {
            publishAnalyticsEventForMinorConversion({
                aiGuideThreadId: threadId ?? "",
                eventSchemaVersion: 1,
                targetMode: targetMode,
                eventType: "AiGuideStudentToggleClicked",
            });
        }
    };

    return (
        <View style={styles.switch}>
            <Tooltip
                content={tooltipContentString}
                forceAnchorFocusivity={false}
                placement="bottom"
            >
                <Switch
                    aria-label={t`Student mode`}
                    checked={userAsStudent}
                    icon={
                        <PhosphorIcon
                            icon={graduationCapIcon}
                            color={
                                light
                                    ? semanticColor.core.foreground.neutral
                                          .subtle
                                    : undefined
                            }
                        />
                    }
                    testId="ai-guide-student-toggle"
                    onChange={handleToggle}
                />
            </Tooltip>
        </View>
    );
};

const styles = StyleSheet.create({
    switch: {
        padding: `${spacing.small_12}px ${spacing.xSmall_8}px`,
    },
});
